import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { API, APIConfig } from 'src/api/API'
import { LocalStorage } from 'src/store/LocalStorage'
import { UserStore } from 'src/store/UserStore'
import { EnvConfig } from 'src/util/EnvConfig'
import { UserUtil } from 'src/util/UserUtil'

let isRefreshInProgress = false

export default abstract class BaseAPI {

  protected client: AxiosInstance
  protected apiConfig: APIConfig
  protected defaultOptions = {
    headers: {
      Accept: 'application/json; version=1.1',
      'Content-Type': 'application/json; version=1.1',
      'X-CLIENT-ID': EnvConfig.getClientID()
    },
    timeout: 60 * 1000, // 60 seconds timeout,
  }

  constructor(apiConfig: APIConfig) {
    this.apiConfig = apiConfig
    this.client = axios.create({
      ...this.getDefaultOptions(),
      baseURL: EnvConfig.getBaseURL(),
    })
  }

  protected getDefaultOptions() {
    return {
      ...this.defaultOptions,
      baseURL: EnvConfig.getBaseURL(),
    }
  }

  protected async refreshAccessToken(refreshToken: string) {
    try {
      const userStore = LocalStorage.getInstance().getUserStore()

      // generate form data for refresh request
      const refreshData = new FormData()
      const clientId = userStore.isAuthMethodSSO ? EnvConfig.getAuth0ClientID() : EnvConfig.getClientID()
      refreshData.append('client_id', clientId)
      refreshData.append('grant_type', 'refresh_token')
      refreshData.append('refresh_token', refreshToken)

      const res = await axios.post(`${EnvConfig.getBaseURL()}/oauth2/token/`, refreshData)

      // update localStorage
      const updatedUserStore = {
        ...userStore,
        accessToken: res.data.access_token,
        refreshToken: res.data.refresh_token,
        isAuthenticated: true,
        authCheckTimestamp: UserUtil.getDefaultAuthCheckTimestamp()
      }
      localStorage.setItem(UserStore.USER_STORE_STORAGE_KEY, JSON.stringify(updatedUserStore))
      return res.data.access_token
    } catch (e) {
      console.error('Unable to refresh token', e)
      window.location.href = `${window.location.origin}/auth/logout`
    }
  }

  protected attachResponseInterceptor() {
    this.client.interceptors.response.use(
      async response => {
        if (response.headers.hasOwnProperty('out-of-coffee')) {
          LocalStorage.getInstance().purgeUserStore()
          if (window.location.href !== `${window.location.origin}/auth`) {
            window.location.reload()
          }
        }
        return response
      },
      async error => {
        if (!error || !error.response || !error.response.status) {
          return
        }

        // Reject promise if non-auth error
        if (error.response.status !== 401) {
          const interceptors = this.apiConfig.responseInterceptors?.filter(interceptor => interceptor.code === error.response.status) ?? []
          for (const interceptor of interceptors) {
            interceptor.callback(interceptor.code, error.response.config.url)
            if (interceptor.shouldCancelRequest) {
              throw new axios.Cancel('Operation cancelled')
            }
          }
          return Promise.reject(error)
        } else {

          // TEAMS-1739 attempt to resolve /auth/logout infinite reloading
          if (/oauth2\/token/.test(error.response.config.url)) {
            window.location.href = `${window.location.origin}/auth`
          }

          if (!/asset|download|oauth2\/token/.test(error.response.config.url) && !isRefreshInProgress) {
            isRefreshInProgress = true
            const refreshToken = LocalStorage.getInstance().getRefreshToken()

            if (refreshToken) {
              const newAccessToken = await this.refreshAccessToken(refreshToken)
              isRefreshInProgress = false
              error.response.config.headers['Authorization'] = `Bearer ${newAccessToken}`
              return axios(error.response.config)
            }
          }

        }
      }
    )
  }

  withAuth() {
    this.client = axios.create({
      ...this.getDefaultOptions(),
      baseURL: EnvConfig.getBaseURL(),
    })
    this.client.interceptors.request.use(async config => {
      const accessTokenExpiration = LocalStorage.getInstance().getAuthCheckTimestamp()
      const refreshToken = LocalStorage.getInstance().getRefreshToken()

      if (refreshToken && Date.now() > accessTokenExpiration) {
        const newAccessToken = await this.refreshAccessToken(refreshToken)
        config.headers = Object.assign({Authorization: `Bearer ${newAccessToken}`}, config.headers)
        return config
      } else {
        config.headers = Object.assign({Authorization: `Bearer ${LocalStorage.getInstance().getAccessToken()}`}, config.headers)
        return config
      }
    }, error => Promise.reject(error))
    this.attachResponseInterceptor()
    return this
  }

  withPublicAPIKey(apiKey: string) {
    this.client = axios.create({
      ...this.getDefaultOptions(),
      baseURL: EnvConfig.getBaseURL(),
    })
    this.client.interceptors.request.use(async config => {
      config.headers = {
        ...config.headers,
        APIKEY: apiKey
      }
      return config
    }, error => Promise.reject(error))
    this.attachResponseInterceptor()
    return this
  }

  withoutAuth() {
    this.client = axios.create({
      ...this.getDefaultOptions(),
      baseURL: EnvConfig.getBaseURL(),
    })
    this.attachResponseInterceptor()
    return this
  }

  async getPage<T = any, R = AxiosResponse<T>, D = any>(url: string, page: number = 1, pageSize: number = 100, config?: AxiosRequestConfig<D>) {
    config = {
      ...config,
      params: {
        page: page,
        page_size: pageSize,
        ...config?.params
      }
    }
    return this.get<T, AxiosResponse<T>, D>(url, config)
  }

  async getAllPages<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<T[]> {

    config = {
      ...config,
      params: {
        ...config?.params,
        page_size: config?.params?.page_size ?? API.DEFAULT_PAGE_SIZE,
        page: config?.params?.page ?? 1
      }
    }

    const pageSize = config.params.page_size
    const page = parseInt(config.params.page)

    const allData: T[] = []

    const res = await this.get<T[], AxiosResponse<T[]>, D>(url, config)
    if (!res || res.status >= 400) {
      throw new Error(`Paginated request failed (page: ${page} of ${pageSize})`)
    }
    if (res.status >= 200) {
      allData.push(...res.data)
      const totalResults = res.headers['total-count'] ? parseInt(res.headers['total-count']) : 0
      const numPages = Math.ceil(totalResults / pageSize)
      if (numPages <= page) {
        return allData
      }
      for (let i = page + 1; i <= numPages; i++) {
        config.params.page = i
        config.params.page_size = pageSize
        const pageRes = await this.getPage<T[], AxiosResponse<T[]>, D>(url, i, pageSize)
        if (!pageRes || pageRes.status >= 400) {
          throw new Error(`Paginated request failed (page: ${i} of ${pageSize}).`)
        }
        if (pageRes && pageRes.status < 400 && pageRes.status >= 200) {
          allData.push(...pageRes.data)
        }
      }
    }
    return allData
  }

  // region Axios client wrappers
  request<T = any, R = AxiosResponse<T>, D = any>(config: AxiosRequestConfig<D>): Promise<R> {
    return this.client.request(config)
  }

  get<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
    return this.client.get(url, config)
  }

  delete<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
    return this.client.delete(url, config)
  }

  head<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
    return this.client.head(url, config)
  }

  options<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
    return this.client.options(url, config)
  }

  post<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
    return this.client.post(url, data, config)
  }

  put<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
    return this.client.put(url, data, config)
  }

  patch<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
    return this.client.patch(url, data, config)
  }
  // endregion Axios client wrappers
}
